import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  IconButton,
  Chip,
  LinearProgress,
  Paper,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import Grid2 from "@mui/material/Unstable_Grid2/Grid2";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { DateTime } from "luxon";
import {
  Person,
  PersonCardInteraction,
  PersonViewType,
} from "@microsoft/mgt-react";
import NoMealsIcon from "@mui/icons-material/NoMeals";
import LocalCafeIcon from "@mui/icons-material/LocalCafe";
import LunchDiningIcon from "@mui/icons-material/LunchDining";
import BreakfastDiningIcon from "@mui/icons-material/BreakfastDining";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import CloseIcon from "@mui/icons-material/Close";
import SelectWorkshop from "./SelectWorkshop/SelectWorkshop";
import RegistrationControls from "./RegistrationControls/RegistrationControls";
import getLocation from "../../helper/WorkshopLocation";
import style from "./Workshop.module.css";
import { unselectWorkshop } from "../../state/workshopSlice";

const Workshop = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const workshop = useSelector((state) => state.workshops.selectedWorkshop);
  const loading = useSelector(
    (state) => state.workshops.status.workshopLoading
  );
  const [displayStyle, setDisplayStyle] = useState(style.workshop);

  const closeWorkshop = () => {
    dispatch(unselectWorkshop());
    navigate("/");
  };

  useEffect(() => {
    workshop !== null
      ? setDisplayStyle(style.workshop)
      : setDisplayStyle(`${style.workshop} ${style.notSelected}`);
  }, [workshop]);

  return (
    <Paper variant="outlined" className={displayStyle}>
      {loading && <LinearProgress />}
      {workshop ? (
        <>
          {/* Header Box */}
          <Box
            sx={{
              bgcolor: "sd36.blue",
              padding: "1.25rem",
              boxShadow: 3,
              display: "flex",
            }}
          >
            <Box sx={{ flexGrow: "1" }}>
              <Typography variant="h1">{workshop.title}</Typography>
              <Typography variant="h5">
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-start",
                    alignItems: "center",
                    gap: "0.5rem",
                    border: "1px solid whitesmoke",
                    backgroundColor: "white",
                    borderRadius: "4px",
                    padding: "0.2rem",
                    margin: "0.2rem",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                    }}
                  >
                    <CalendarMonthIcon fontSize="inherit" />
                    <Box>
                      {DateTime.fromISO(workshop.startDatetime).toLocaleString(
                        DateTime.DATE_MED_WITH_WEEKDAY
                      )}
                    </Box>{" "}
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                    }}
                  >
                    <AccessTimeIcon fontSize="inherit" />
                    <Box>
                      {DateTime.fromISO(workshop.startDatetime).toLocaleString(
                        DateTime.TIME_SIMPLE
                      )}{" "}
                      -{" "}
                      {DateTime.fromISO(workshop.endDatetime).toLocaleString(
                        DateTime.TIME_SIMPLE
                      )}
                    </Box>
                  </Box>
                </Box>
              </Typography>
              <Chip
                label={workshop.category.name}
                size="small"
                color="default"
                variant="contained"
                sx={{ marginTop: "0.25rem" }}
              />
            </Box>
            <Box sx={{ margin: "-1rem" }}>
              <IconButton aria-label="Close workshop" onClick={closeWorkshop}>
                <CloseIcon fontSize="large" />
              </IconButton>
            </Box>
          </Box>
          {!loading && (
            <Grid2 container spacing={1} sx={{ margin: "0.5rem" }}>
              <Grid2 xs={12}>
                <RegistrationControls workshop={workshop} />
              </Grid2>
              {/* Body */}

              <Grid2
                xs={12}
                // sx={{ margin: "0.5rem 1rem" }}
                className={style.section}
              >
                <Box>
                  <Typography variant="h6" className={style.sectionHeader}>
                    Workshop Date
                  </Typography>
                  <Box className={style.sectionBody}>
                    <Typography variant="h4">
                      {DateTime.fromISO(workshop.startDatetime).toLocaleString(
                        DateTime.DATE_MED_WITH_WEEKDAY
                      )}
                    </Typography>
                    <Typography variant="h5">
                      {DateTime.fromISO(workshop.startDatetime).toLocaleString(
                        DateTime.TIME_SIMPLE
                      )}{" "}
                      -{" "}
                      {DateTime.fromISO(workshop.endDatetime).toLocaleString(
                        DateTime.TIME_SIMPLE
                      )}
                    </Typography>
                  </Box>
                </Box>
              </Grid2>

              <Grid2 xs={12} className={style.section}>
                <Typography variant="h6" className={style.sectionHeader}>
                  Description
                </Typography>
                <Typography
                  variant="body1"
                  dangerouslySetInnerHTML={{ __html: workshop.description }}
                  className={style.sectionBody}
                ></Typography>
              </Grid2>

              <Grid2 xs={12} sm={12} md={5} xl={4} className={style.section}>
                <Typography variant="h6" className={style.sectionHeader}>
                  Presented by
                </Typography>
                <Box
                  className={`${style.sectionBody} ${style.sectionBodyPeople}`}
                >
                  {workshop.presenters.length === 0 ? (
                    <Typography
                      variant="h5"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        gap: "0.5rem",
                      }}
                    >
                      <AccountCircleIcon fontSize="large" />
                      {workshop.externalPresenter}
                    </Typography>
                  ) : (
                    workshop.presenters.map((w, index) => (
                      <Box sx={{ paddingBottom: "0.5rem" }} key={index}>
                        <Person
                          view={PersonViewType.twolines}
                          line2Property="jobTitle"
                          personQuery={w.staff.mail}
                          personCardInteraction={PersonCardInteraction.click}
                        ></Person>
                      </Box>
                    ))
                  )}
                </Box>
              </Grid2>
              <Grid2 xs={12} sm={12} md={5} xl={4} className={style.section}>
                {workshop?.organizer?.mail && (
                  <>
                    <Typography variant="h6" className={style.sectionHeader}>
                      Organized by
                    </Typography>
                    <Box
                      className={`${style.sectionBody} ${style.sectionBodyPeople}`}
                    >
                      <Person
                        view={PersonViewType.twolines}
                        line2Property="jobTitle"
                        personQuery={workshop.organizer.mail}
                        personCardInteraction={PersonCardInteraction.click}
                      ></Person>
                    </Box>
                  </>
                )}
              </Grid2>
              <Grid2 xs={12} className={style.section}>
                <Typography variant="h6" className={style.sectionHeader}>
                  Location
                </Typography>
                <Typography className={style.sectionBody}>
                  {getLocation(workshop)}
                </Typography>
              </Grid2>
              {workshop.locationDescription && (
                <Grid2 xs={12} className={style.section}>
                  <Typography variant="h6" className={style.sectionHeader}>
                    Additional location information
                  </Typography>
                  <Typography className={style.sectionBody}>
                    {workshop.locationDescription}
                  </Typography>
                </Grid2>
              )}

              {workshop?.focusArea && (
                <Grid2 xs={12} sm={12} md={5} xl={4} className={style.section}>
                  <Typography variant="h6" className={style.sectionHeader}>
                    Focus area
                  </Typography>
                  <Typography className={style.sectionBody}>
                    {workshop.focusArea}
                  </Typography>
                </Grid2>
              )}

              {workshop?.targetAudience && (
                <Grid2 xs={12} sm={12} md={5} xl={4} className={style.section}>
                  <Typography variant="h6" className={style.sectionHeader}>
                    Target Audience
                  </Typography>
                  <Typography className={style.sectionBody}>
                    {workshop.targetAudience}
                  </Typography>
                </Grid2>
              )}

              <Grid2 xs={12} sm={12} md={5} xl={4} className={style.section}>
                <Typography variant="h6" className={style.sectionHeader}>
                  Meals provided
                </Typography>
                <Box className={style.sectionBody}>
                  {!workshop.breakfast &&
                  !workshop.lunch &&
                  !workshop.snack &&
                  !workshop.dinner ? (
                    <Chip icon={<NoMealsIcon />} label="None" size="small" />
                  ) : (
                    <>
                      {workshop.breakfast && (
                        <Chip
                          icon={<BreakfastDiningIcon />}
                          label="Breakfast"
                          size="small"
                          className={style.meal}
                        />
                      )}
                      {workshop.snack && (
                        <Chip
                          icon={<LocalCafeIcon />}
                          label="Snack"
                          size="small"
                          className={style.meal}
                        />
                      )}
                      {workshop.lunch && (
                        <Chip
                          icon={<LunchDiningIcon />}
                          label="Lunch"
                          size="small"
                          className={style.meal}
                        />
                      )}
                      {workshop.dinner && (
                        <Chip
                          icon={<RestaurantIcon />}
                          label="Dinner"
                          size="small"
                          className={style.meal}
                        />
                      )}
                    </>
                  )}
                </Box>
              </Grid2>
              <Box className={style.footer}>
                Workshop ID: {workshop.workshopId}
              </Box>
            </Grid2>
          )}
        </>
      ) : (
        <SelectWorkshop />
      )}
    </Paper>
  );
};

export default Workshop;
